import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, Section, Title, theme } from '../../styles/GlobalStyles';

const StatsContainer = styled.div`
  background: ${theme.gradients.primary};
  color: ${theme.colors.text};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%2306b6d4" fill-opacity="0.05"><path d="M50 0v100M0 50h100"/></g></g></svg>');
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 70%, rgba(6, 182, 212, 0.1) 0%, transparent 50%);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 30px;
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 20px;
`;

const StatNumber = styled(motion.div)`
  font-size: 3.5rem;
  font-weight: 800;
  color: ${theme.colors.accent};
  margin-bottom: 10px;
  line-height: 1;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2.8rem;
  }
`;

const StatLabel = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 10px;
  color: ${theme.colors.text};
`;

const StatDescription = styled.p`
  font-size: 1rem;
  color: ${theme.colors.textSecondary};
  line-height: 1.5;
`;

interface CounterProps {
  end: number;
  duration: number;
  suffix?: string;
}

const Counter: React.FC<CounterProps> = ({ end, duration, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationId: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [end, duration]);

  return <>{count}{suffix}</>;
};

const Statistics: React.FC = () => {
  const stats = [
    {
      number: 100,
      suffix: 's',
      label: 'Platforms & Apps',
      description: 'Being tested and scanned continuously for vulnerabilities and security compliance'
    },
    {
      number: 1000,
      suffix: 's',
      label: 'Endpoints & Servers',
      description: 'Protected and monitored 24x7 with our advanced security technologies'
    },
    {
      number: 1500,
      suffix: '+',
      label: 'Individuals and Defenders',
      description: 'Trained in cybersecurity best practices and threat awareness programs'
    }
  ];

  return (
    <StatsContainer>
      <Section>
        <Container>
          <Title style={{ marginBottom: '60px' }}>
            Our Impact in Numbers
          </Title>
          
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <StatNumber
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
                >
                  <Counter 
                    end={stat.number} 
                    duration={2} 
                    suffix={stat.suffix}
                  />
                </StatNumber>
                <StatLabel>{stat.label}</StatLabel>
                <StatDescription>{stat.description}</StatDescription>
              </StatCard>
            ))}
          </StatsGrid>
        </Container>
      </Section>
    </StatsContainer>
  );
};

export default Statistics;

